/* eslint-disable react/no-multi-comp */
import _ from 'lodash'
import React from 'react'
import styled from '@emotion/styled'
import { Button, SVGIcon, Checkbox, colors, Text } from 'ic-snacks'
import { FormattedMessage } from 'react-intl'
import eq from 'lodash/eq'
import each from 'lodash/each'
import { type RowInfo, type FinalState } from 'react-table'
import {
  withNotificationsContext,
  type NotificationsContextValue,
} from 'legacy/contexts/notifications/NotificationsContext'
import FilterBar from 'legacy/components/FilterBar'
import instacart from 'legacy/common/instacart'
import withRetailerRouter from 'legacy/common/withRouter'
import Spinner from 'legacy/components/Spinner'
import Link from 'legacy/components/Link'
import RegionLocationDropdown from 'legacy/components/RegionLocationDropdown'
import Table from 'legacy/components/Table/Table'
import TableActions from 'legacy/components/Table/TableActions'
import deprecatedAnalytics from 'legacy/common/deprecatedAnalytics'
import ApiResponseError from 'legacy/common/errors/ApiResponseError'
import { type RetailerRouteComponentProps } from 'legacy/components/RetailerRoute'
import { YES_NO_OPTIONS } from 'legacy/common/constants'
import { withTrackEventOnMount } from 'utils/events/hocs'
import { COST_UNIT_OPTIONS } from 'legacy/common/utils/catalog/units'
import { Items, type Item } from 'legacy/routes/catalog/types/items.types'
import { type ProductFormValues } from 'legacy/routes/catalog/types/product.types'
import ProductLocationsCell from './ProductLocationsCell'
import BulkEditProductLocations from './BulkEditProductLocationsModal'
import AddProductToLocations from './AddProductToLocations'
import { WarehouseContext } from 'utils/contexts/warehouse/WarehouseContext'
import { type Country } from 'legacy/common/types'
import { CountryId } from 'legacy/common/utils/countries/countries.types'
import { withProductLocationsHelper } from 'legacy/routes/catalog/products/product/edit/ProductLocationsHelper'

const Container = styled.div`
  border-left: 1px solid ${colors.GRAY_93};
  flex: 1;
`

const BUTTON_STYLE = {
  paddingLeft: '0',
  paddingRight: '0',
  paddingTop: '0',
  paddingBottom: '0',
}

const BooleanText = styled.span`
  padding: 0 10px;
`

const countryIdToDomain: { [key in string]: string } = {
  [CountryId.CANADA]: 'https://instacart.ca',
  [CountryId.USA]: 'https://instacart.com',
}

interface BooleanTextCellProps {
  value: boolean
}

function BooleanTextCell({ value }: BooleanTextCellProps) {
  return <BooleanText>{value ? 'Yes' : 'No'}</BooleanText>
}

export interface Query {
  item_ids: string
  inventory_area_ids: string
  region_ids: string
}

interface Props extends NotificationsContextValue, RetailerRouteComponentProps<{}, Query> {
  partnerId: string
  warehouseId: string
  productId: string
  product: ProductFormValues
  className?: string
  // All following props come from withProductLocationsHelper
  data?: Item[]
  isLoading?: boolean
  isError?: boolean
  refetch?: () => void
  itemIds?: number[]
}

interface State {
  selected: number[]
  currentRowEditIndex: number
  itemDrafts: {
    [index: string]: string
  }
  activeModal: 'addLocations' | 'bulkEditLocations' | null
}

class ProductLocations extends React.Component<Props, State> {
  readonly state: State = {
    selected: [],
    currentRowEditIndex: -1,
    itemDrafts: {},
    activeModal: null,
  }

  componentDidMount() {
    deprecatedAnalytics.track('product.warehouse_location_view', {
      product_id: this.props.productId,
    })
  }

  columns = (country?: Country) => [
    {
      Header: () => (
        <Checkbox
          id="checkboxSelectAll"
          isSelected={this.areAllItemsSelected()}
          onChange={this.handleSelectAllClick}
        />
      ),
      width: 40,
      Cell: ({ original }: RowInfo) => (
        <div>
          <Checkbox
            id={`checkbox-${original.id}`}
            isSelected={this.state.selected.includes(original.id)}
            onChange={() => this.onSelectItem(original.id)}
          />
        </div>
      ),
    },
    {
      header: 'store',
      Cell: ({ original }: RowInfo) => (
        <Link href={country && `${countryIdToDomain[country.id]}/store/items/${original.id}`}>
          {original.inventory_area.name}
        </Link>
      ),
    },
    {
      id: 'codes',
      header: 'Available',
      width: 160,
      Cell: ({ original, index }: RowInfo) => {
        if (index === this.state.currentRowEditIndex) {
          return (
            <ProductLocationsCell
              attribute="available"
              type="dropdown"
              options={YES_NO_OPTIONS}
              item={original}
              onChange={this.handleEdit}
            />
          )
        }

        return <BooleanTextCell value={original.available} />
      },
    },
    {
      header: 'Taxable',
      Cell: ({ original, index }: RowInfo) => {
        if (index === this.state.currentRowEditIndex) {
          return (
            <ProductLocationsCell
              attribute="taxable"
              type="dropdown"
              options={YES_NO_OPTIONS}
              item={original}
              onChange={this.handleEdit}
            />
          )
        }

        return <BooleanTextCell value={original.taxable} />
      },
      width: 160,
    },
    {
      header: 'Par Weight Adjusted',
      width: 220,
      Cell: (a: RowInfo) => {
        const { original } = a

        return (
          <span>{original.product_type === 'variable' ? 'Yes' : original.id ? 'No' : '-'}</span>
        )
      },
    },
    {
      header: 'Standard Price',
      width: 160,
      Cell: ({ original, index }: RowInfo) => {
        if (index === this.state.currentRowEditIndex) {
          return (
            <ProductLocationsCell
              attribute="cost_price_per_unit"
              type="currency"
              item={original}
              onChange={this.handleEdit}
            />
          )
        }

        return <span>${original.cost_price_per_unit}</span>
      },
    },
    {
      header: 'Displayed Price',
      width: 160,
      Cell: ({ original }: RowInfo) => <span>${original.price}</span>,
    },
    {
      header: 'Priced By',
      width: 150,
      Cell: ({ original, index }: RowInfo) => {
        if (index === this.state.currentRowEditIndex) {
          return (
            <ProductLocationsCell
              attribute="cost_unit"
              type="dropdown"
              options={COST_UNIT_OPTIONS}
              item={original}
              onChange={this.handleEdit}
            />
          )
        }

        return <span>{original.cost_unit}</span>
      },
    },
    {
      Header: () => null,
      width: 160,
      Cell: ({ index }: RowInfo) => {
        if (this.state.selected.length) {
          return null
        }

        if (this.state.currentRowEditIndex === index) {
          return (
            <TableActions>
              <Button
                style={BUTTON_STYLE}
                icon={<SVGIcon name="xCircle" color={colors.GRAY_46} />}
                snacksStyle="flat"
                onClick={() => this.handleEditRowClick(index)}
              />
              <Button
                style={BUTTON_STYLE}
                icon={<SVGIcon name="checkmarkCircleFilled" color={colors.GREEN_500} />}
                snacksStyle="flat"
                onClick={this.handleConfirmEditClick}
              />
            </TableActions>
          )
        }

        return (
          <TableActions>
            <Button
              style={BUTTON_STYLE}
              icon={<SVGIcon name="pencil" color={colors.GREEN_500} />}
              snacksStyle="flat"
              onClick={() => this.handleEditRowClick(index)}
            />
          </TableActions>
        )
      },
    },
  ]

  handleEditRowClick = (currentRowEditIndex: number) => {
    this.setState(state => ({
      currentRowEditIndex:
        currentRowEditIndex === state.currentRowEditIndex ? -1 : currentRowEditIndex,
      itemDrafts: {},
    }))
  }

  handleEdit = (attr: string, value: string) => {
    this.setState(state => ({
      itemDrafts: {
        ...state.itemDrafts,
        [attr]: value,
      },
    }))
  }

  handleModalClose = () => {
    this.setState({
      activeModal: null,
    })
  }

  handleConfirmEditClick = async () => {
    const { productId, partnerId, warehouseId, data, refetch } = this.props
    const { currentRowEditIndex } = this.state
    const editedItem = data[currentRowEditIndex]
    try {
      const item: Item = await instacart.go.put(
        `/v1/partners/${partnerId}/warehouses/${warehouseId}/products/${productId}/items/${editedItem.id}`,
        { data: this.state.itemDrafts }
      )
      each(this.state.itemDrafts, (attribute, value) => {
        deprecatedAnalytics.track('product.warehouse_location_change', {
          source_type: attribute,
          source_value: value,
          item_id: item.id,
        })
      })

      this.props.notify(<FormattedMessage id="catalog.products.locations.requestSubmitted" />)

      refetch()
    } catch (e) {
      if (e instanceof ApiResponseError && e.errors && e.errors.length) {
        this.props.notifyError(e.errors[0])
      }
    }
  }

  onSelectItem = (item_id: number) => {
    this.setState(state => {
      const selected = [...state.selected]

      if (selected.includes(item_id)) {
        const i = selected.indexOf(item_id)
        selected.splice(i, 1)
      } else {
        selected.push(item_id)
      }

      return { selected }
    })
  }

  handleSelectAllClick = () => {
    this.setState(state => ({
      itemDrafts: {},
      currentRowEditIndex: -1,
      selected: this.areAllItemsSelected() ? [] : [...this.props.itemIds],
    }))
  }

  areAllItemsSelected = () => {
    if (!this.props.itemIds.length) return false

    return eq(this.props.itemIds.sort().toString(), this.state.selected.sort().toString())
  }

  handleBulkEditClick = () => {
    this.setState({
      activeModal: 'bulkEditLocations',
    })
  }

  handleAddLocationClick = () => {
    this.setState({
      activeModal: 'addLocations',
    })
  }

  getTrProps = (_: FinalState, b?: RowInfo) => ({
    style: {
      padding: '0 0 0 20px',
      backgroundColor: b && this.state.selected.includes(b.original.id) ? '#eff5eb' : 'inherit',
    },
  })

  render() {
    const { selected, activeModal } = this.state
    const { partnerId, warehouseId, productId, query, className, data, isLoading, isError } =
      this.props

    if (!data) return <Spinner centered />

    // If we have too many locations selected, then the request might time out
    // In this case, we want to prompt them to select a specific location.
    // If they have a specific location selected and they still get an error, we just
    // want to show them the default error message.
    const customErrorMessage =
      isError && !query.region_ids && !query.inventory_area_ids
        ? 'Too many locations selected. Please select a specific region or location and try again.'
        : undefined

    return (
      <Container className={className}>
        <FilterBar padding="15px 20px">
          <div>
            {selected.length ? (
              <Text variant="T.16" style={{ color: colors.GREEN_500 }}>
                <FormattedMessage
                  id="catalog.products.locations.items.selected"
                  values={{ count: selected.length }}
                />
              </Text>
            ) : (
              <RegionLocationDropdown useInventoryAreas />
            )}
          </div>
          <div>
            {!!selected.length && (
              <Button snacksStyle="primary" size="small" onClick={this.handleBulkEditClick}>
                <FormattedMessage
                  id="catalog.products.locations.bulk-edit"
                  values={{ count: selected.length }}
                />
              </Button>
            )}
            {!selected.length && (
              <Button snacksStyle="primary" size="small" onClick={this.handleAddLocationClick}>
                <FormattedMessage id="catalog.products.locations.add-locations" />
              </Button>
            )}
          </div>
        </FilterBar>

        <WarehouseContext.Consumer>
          {warehouseContext => {
            if (!warehouseContext) {
              throw new Error('WarehouseContext not provided')
            }

            return (
              <Table
                columns={this.columns(
                  warehouseContext.warehouse && warehouseContext.warehouse.country
                )}
                data={data}
                isLoading={isLoading}
                isError={isError}
                errorMessage={customErrorMessage}
                paginated={false}
                getTrProps={this.getTrProps}
                noDataText="No Store Locations"
              />
            )
          }}
        </WarehouseContext.Consumer>
        {activeModal === 'addLocations' ? (
          <AddProductToLocations
            partnerId={partnerId}
            warehouseId={warehouseId}
            productId={productId}
            onClose={this.handleModalClose}
          />
        ) : activeModal === 'bulkEditLocations' ? (
          <BulkEditProductLocations
            partnerId={partnerId}
            warehouseId={warehouseId}
            productId={productId}
            itemIds={selected}
            onClose={this.handleModalClose}
          />
        ) : null}
      </Container>
    )
  }
}

export default withTrackEventOnMount({
  id: 'catalog.products.store_locations.viewed',
  description: 'Viewed the store locations page for a specific product',
})(withRetailerRouter(withNotificationsContext(withProductLocationsHelper(ProductLocations))))
